import { Box, styled } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { LwButton, LwSplitButton } from 'redesign';
import { FilterButton } from 'ogp/components/views/administration/shared';
import { useState } from 'react';
import { useToggle } from 'shared/hooks';
import { DateTime } from 'luxon';
import { QUERY_KEYS } from 'api/query-keys';
import { useQueryClient } from 'react-query';
import { WeekDateControl } from 'shared/components/date-control-toolbox/week-date-control/week-date-control';
import { TableDateControl } from './components/table-date-control/table-date-control';
import { PlanningViewSwitcher } from './components/planning-view-switcher';
import { useGetActions } from './hooks/use-get-actions';
import { PlanningFilterDataProvider } from './components/filters/planning-filter-data-provider';
import { ExportModal } from './components/export-drawer';
import { usePlanningParamsContext } from '../providers/planning-params-provider';

type CalendarQueryPaginatedData = { pages: any[]; pageParams: any[] } | undefined;

export const PlanningToolBox = () => {
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const { on: filtersOpen, toggle: toggleFilters } = useToggle();
  const location = useLocation();
  const activeRoute = location.pathname.split('/').pop();
  const { actions, primaryButtonAction } = useGetActions(activeRoute);
  const { selectedFiltersCount } = usePlanningParamsContext();
  const queryClient = useQueryClient();

  const { filters, setFilters } = usePlanningParamsContext();

  const selectedDate = filters.date ? filters.date : DateTime.now();

  const trimShiftPlanningCache = () => {
    queryClient.setQueryData(
      QUERY_KEYS.shiftPlanning({
        branchOffices: filters.branchOfficeIds,
        departments: filters.departmentIds,
        fromDate: selectedDate.startOf('week').toISODate(),
        toDate: selectedDate.endOf('week').toISODate(),
        jobType: filters.jobType,
        hasApplicants: filters.hasApplicants,
        filledStatus: filters.filledStatus,
        partnerId: filters.partner,
        isFreelance: filters.isFreelance,
        isTempWork: filters.isTempWork,
      }),
      (old: CalendarQueryPaginatedData) => {
        if (!old) {
          return { pages: [], pageParams: [] };
        }
        return {
          ...old,
          pages: [old.pages[0]],
          pageParams: [old.pageParams[0]],
        };
      }
    );
  };

  const handleChangeDate = (newDate: DateTime) => {
    trimShiftPlanningCache();
    setFilters({ date: newDate });
  };

  return (
    <>
      <Header height={100}>
        <LwSplitButton
          onClick={primaryButtonAction.onClick}
          options={actions}
          primaryLabel={primaryButtonAction.label}
          disabledPrimary={primaryButtonAction.disabled}
          disabledDropdown={actions.length === 0}
          dataTestId="shift-action"
        />
        {activeRoute === 'table' ? (
          <TableDateControl />
        ) : (
          <WeekDateControl
            date={selectedDate}
            onChange={handleChangeDate}
            onToday={() => handleChangeDate(DateTime.now())}
          />
        )}
        <ButtonSection>
          <PlanningViewSwitcher />
          <LwButton color="secondary" onClick={() => setExportModalOpen(true)}>
            Export
          </LwButton>
          <FilterButton onClick={toggleFilters} nrOfActiveFilters={selectedFiltersCount} />
        </ButtonSection>
      </Header>
      {filtersOpen ? <PlanningFilterDataProvider toggleFilters={toggleFilters} /> : null}
      {exportModalOpen ? (
        <ExportModal
          setExportModalOpen={() => setExportModalOpen(false)}
          exportModalOpen={exportModalOpen}
        />
      ) : null}
    </>
  );
};

const Header = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  marginTop: theme.spacing(8),
  gap: theme.spacing(8),
  width: '100%',
  justifyContent: 'space-between',
}));

const ButtonSection = styled(Box)(({ theme }) => ({
  gap: theme.spacing(8),
  display: 'flex',
  flexShrink: 0,
}));
